import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { latestUnusedBrief } from '@/domain/workflow/home';
import type { CounselingGoal, NextSessionBrief, PlannedQuestion } from '@/domain/schema/v1';
import { goalStatusLabel } from '@/domain/workflow/documents';
import { uuidv7 } from 'uuidv7';
import { useAutosave } from '../../hooks/useAutosave';
import { useLoad } from '../../hooks/useLoad';
import { useService } from '../../hooks/useService';
import { LinesInput, SaveIndicator, TextArea } from '../../components/fields';
import { TreatmentLine } from '../../components/TreatmentLine';
import { pendingInterpretationTalks } from '@/domain/ai/assessment';

/** 다음 회기 준비 (UX_FLOW §7): 상담 직전 이 화면 하나로 준비한다. 회기별 준비 자료를 골라 볼 수 있고, 사용 완료한 것도 남는다 */
export function BriefScreen() {
  const { clientId = '' } = useParams();
  const svc = useService();
  const [params, setParams] = useSearchParams();
  const [err, setErr] = useState<string | null>(null);
  const { data, reload } = useLoad(async () => {
    const [client, briefs, goals, sessions, assessments] = await Promise.all([
      svc.getClient(clientId),
      svc.listBriefs(clientId),
      svc.listGoals(clientId),
      svc.listSessions(clientId),
      svc.listAssessments(clientId),
    ]);
    // 상담기록지는 있는데 다음 회기 준비 자료가 아직 없는 가장 최근 회기 (만들기 버튼용)
    const withBrief = new Set(briefs.map((b) => b.sourceSessionId));
    let missing: (typeof sessions)[number] | undefined;
    for (const s of [...sessions].reverse()) {
      if (withBrief.has(s.id)) break;
      if (await svc.currentJournal(s.id)) {
        missing = s;
        break;
      }
    }
    const talks = pendingInterpretationTalks(
      assessments,
      sessions.filter((s) => s.status === 'approved').map((s) => s.sessionDate),
    );
    return { client, briefs, goals, missing, talks };
  }, [svc, clientId]);
  if (!data) return <p className="muted">불러오는 중…</p>;
  const wanted = Number(params.get('target')) || undefined;
  const brief =
    (wanted ? data.briefs.find((b) => b.targetSessionNumber === wanted) : undefined) ??
    latestUnusedBrief(data.briefs) ??
    data.briefs[0];
  const make = (sessionId: string) =>
    void svc
      .generateBrief(sessionId)
      .then((b) => {
        setParams({ target: String(b.targetSessionNumber) });
        reload();
      })
      .catch((e: Error) => setErr(e.message));
  return (
    <section className="stack">
      <div className="page-head">
        <div>
          <p className="eyebrow">
            <Link to={`/clients/${clientId}`}>‹ {data.client.clientCode}</Link>
          </p>
          <h1 className="page-title">다음 회기 준비</h1>
          <p className="muted">
            {data.client.clientCode}
            {brief ? ` · ${brief.targetSessionNumber}회기` : ''}
          </p>
        </div>
        <Link to={`/clients/${clientId}`}>내담자 상세</Link>
      </div>
      {data.briefs.length > 1 && (
        <div className="row" role="tablist" aria-label="회기별 준비 자료">
          {[...data.briefs]
            .sort((a, b) => a.targetSessionNumber - b.targetSessionNumber)
            .map((b) => (
              <button
                key={b.id}
                type="button"
                className={b.id === brief?.id ? 'pill-btn on' : 'pill-btn'}
                onClick={() => setParams({ target: String(b.targetSessionNumber) })}
              >
                {b.targetSessionNumber}회기 준비{b.status === 'used' ? ' ✓' : ''}
              </button>
            ))}
        </div>
      )}
      {data.missing && (
        <div className="card row">
          <span className="small">
            {data.missing.sessionNumber}회기 기록으로 {data.missing.sessionNumber + 1}회기 준비 자료를 아직 만들지
            않았습니다.
          </span>
          <button type="button" className="primary" onClick={() => make(data.missing!.id)}>
            {data.missing.sessionNumber + 1}회기 준비 자료 만들기
          </button>
        </div>
      )}
      {err && (
        <p role="alert" className="warn small">
          {err}
        </p>
      )}
      {brief ? (
        <BriefView key={brief.id} brief={brief} goals={data.goals} onUsed={reload} talks={data.talks} />
      ) : (
        !data.missing && (
          <p className="card muted">
            준비 자료가 없습니다. 회기의 상담기록지를 만들면 여기서 다음 회기 준비 자료를 만들 수 있습니다.
          </p>
        )
      )}
      {!brief && <TreatmentLine clientId={clientId} />}
    </section>
  );
}

/** 예전에 만들어진 준비 자료에 남은 'AI 제안' 표시는 보여주지 않는다 */
const stripAiTag = (x: string) =>
  x
    .replace(/\s*\(AI 제안\)\s*/g, ' ')
    .replace(/(^|\s·\s|\s—\s)AI 제안(?=$|\s·\s)/g, '$1')
    .replace(/(\s·\s|\s—\s)+$/, '')
    .trim();

/**
 * 준비 자료 보기 (운영 개편 v1): 왼쪽(폰은 위) 이번 회기에 할 것 — 위험 확인 · 이번 회기 목표 · 확인할 질문 · 개입 · 미완료 주제.
 * 오른쪽(폰은 아래) 참고 — 지금 치료 단계 · 지난 회기 핵심 · 목표 · 최근 변화 · 사례개념화 · 확인 중인 가설.
 */
function BriefView({
  brief,
  goals,
  onUsed,
  talks,
}: {
  brief: NextSessionBrief;
  goals: CounselingGoal[];
  onUsed: () => void;
  /** 이번 회기에 할 검사 해석 상담 (A35) */
  talks: { testName: string; lines: string }[];
}) {
  const svc = useService();
  const a = useAutosave<NextSessionBrief>(brief, (patch, rev) => svc.updateBrief(brief.id, patch, rev));
  const v = a.value!;
  const [editing, setEditing] = useState(false);
  const active = goals.filter((g) => (v.activeGoalIds ?? []).includes(g.id));
  const clean = (raw: string[] | undefined) => (raw ?? []).map(stripAiTag).filter(Boolean);
  const objectives = clean(v.sessionObjectives);
  const questions = v.assessmentQuestions ?? [];
  const interventions = (v.interventionSuggestions ?? []).map((i) =>
    [i.intervention, i.orientation, i.rationale, i.caution && `주의: ${i.caution}`].filter(Boolean).join(' · '),
  );
  const listCard = (title: string, items: string[], note?: string) =>
    items.length ? (
      <div className="card">
        <strong>{title}</strong>
        {note && <span className="muted small">{note}</span>}
        <ul className="plain-list small">
          {items.map((x) => (
            <li key={x}>· {x}</li>
          ))}
        </ul>
      </div>
    ) : null;

  return (
    <div className="brief-layout">
      <div className="stack">
        <div className="row brief-tools">
          <span className="muted small">
            {v.generatedBy === 'rule' ? '자동으로 만든 준비 자료' : '상담자가 고친 준비 자료'}
          </span>
          <SaveIndicator status={a.status} error={a.error} />
          <span className="grow" />
          <button type="button" className="soft-btn" onClick={() => setEditing((e) => !e)}>
            {editing ? '다 고쳤어요' : '고치기'}
          </button>
        </div>
        {editing ? (
          <div className="card">
            <TextArea
              label="지난 회기 핵심"
              value={v.previousSessionSummary}
              onChange={(x) => a.set('previousSessionSummary', x || undefined)}
            />
            <LinesInput
              label="이번 회기 목표"
              value={v.sessionObjectives}
              onChange={(x) => a.set('sessionObjectives', x)}
            />
            <QuestionsEditor value={v.assessmentQuestions} onChange={(x) => a.set('assessmentQuestions', x)} />
            <LinesInput label="미완료 주제" value={v.unfinishedTopics} onChange={(x) => a.set('unfinishedTopics', x)} />
            <LinesInput label="위험 관련 확인" value={v.riskFollowUp} onChange={(x) => a.set('riskFollowUp', x)} />
          </div>
        ) : (
          <>
            {(v.riskFollowUp ?? []).length > 0 && (
              <div className="card warn-card">
                <strong>위험 관련 확인</strong>
                <ul className="plain-list small">
                  {clean(v.riskFollowUp).map((x) => (
                    <li key={x}>· {x}</li>
                  ))}
                </ul>
              </div>
            )}
            {talks.map((t) => (
              <div key={t.testName} className="card soft-card">
                <strong>검사 해석 상담 · {t.testName}</strong>
                <span className="small">
                  해석을 확인했어요. 이번 회기에 결과를 함께 나눠요. 그대로 말할 수 있는 문장:
                </span>
                <p className="pre small" style={{ margin: 0 }}>
                  {t.lines}
                </p>
              </div>
            ))}
            <div className="step-hero">
              <span className="hero-sub">이번 회기 목표</span>
              {objectives.length ? (
                <ol className="hero-list">
                  {objectives.map((o) => (
                    <li key={o}>{o}</li>
                  ))}
                </ol>
              ) : (
                <p className="hero-guide">지난 회기 계획이 비어 있어요. "고치기"에서 적어 두세요.</p>
              )}
            </div>
            {questions.length > 0 && (
              <div className="card">
                <strong>확인할 질문 {questions.length}</strong>
                <ul className="q-list">
                  {questions.map((q) => (
                    <li key={q.id}>
                      <span className="q-box" aria-hidden />
                      <span>
                        {stripAiTag(q.question)}
                        {q.purpose && <span className="muted small"> · {q.purpose}</span>}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {listCard('고려할 개입', interventions)}
            {listCard('미완료 주제', clean(v.unfinishedTopics))}
          </>
        )}
        <div className="brief-actions">
          <Link className="button primary run-next" to={`/clients/${brief.clientId}/run`}>
            상담 시작 ▶ {brief.targetSessionNumber}회기 진행
          </Link>
          {v.status !== 'used' ? (
            <button
              type="button"
              className="link small"
              onClick={() =>
                void a
                  .flush()
                  .then(() => svc.markBriefUsed(brief.id))
                  .then(onUsed)
              }
            >
              준비 완료로 표시
            </button>
          ) : (
            <p className="muted small">이 준비 자료는 상담에 사용했어요 (내용은 그대로 남아 있어요).</p>
          )}
        </div>
      </div>
      <aside className="stack">
        <TreatmentLine clientId={brief.clientId} />
        <div className="card">
          <strong>지난 회기 핵심</strong>
          <p className="pre small" style={{ margin: 0 }}>
            {v.previousSessionSummary ?? '기록 없음'}
          </p>
        </div>
        <div className="card">
          <strong>현재 목표</strong>
          {active.length ? (
            <ul className="plain-list small">
              {active.map((g) => (
                <li key={g.id}>
                  · {g.title} <span className="status-pill">{goalStatusLabel(g.status)}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="muted small">활성 목표 없음</p>
          )}
        </div>
        {listCard('최근 변화', clean(v.recentChanges))}
        {v.formulationSummary && (
          <div className="card">
            <strong>현재 사례개념화</strong>
            <p className="small" style={{ margin: 0 }}>
              {v.formulationSummary}
            </p>
          </div>
        )}
        {listCard('확인 중인 가설', clean(v.hypothesesToTest), '사실이 아니라 확인할 가설이에요.')}
      </aside>
    </div>
  );
}

function QuestionsEditor({
  value,
  onChange,
}: {
  value: PlannedQuestion[] | undefined;
  onChange: (v: PlannedQuestion[]) => void;
}) {
  const items = value ?? [];
  return (
    <div className="field">
      <div className="field-label">
        <span>추천 질문</span>
      </div>
      {items.map((q, i) => (
        <div key={q.id} className="row">
          <input
            aria-label={`질문 ${i + 1}`}
            value={q.question}
            onChange={(e) => onChange(items.map((x, j) => (j === i ? { ...x, question: e.target.value } : x)))}
          />
          <button type="button" aria-label="삭제" onClick={() => onChange(items.filter((_, j) => j !== i))}>
            ×
          </button>
        </div>
      ))}
      <button type="button" onClick={() => onChange([...items, { id: uuidv7(), question: '질문' }])}>
        + 질문 추가
      </button>
    </div>
  );
}
